import type { TimeGridEvent } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import type Leave from 'modules/shift_management/store/internal/models/Leave'
import type RestDay from 'modules/shift_management/store/internal/models/RestDay'
import type Shift from 'modules/shift_management/store/internal/models/Shift'
import type Holiday from 'modules/shift_management/store/internal/models/Holiday'
import type ShiftsConflicts from 'modules/shift_management/store/internal/collections/ShiftsConflicts'
import type { View } from 'modules/shift_management/lib/types'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'

export type GridEventType = 'shift' | 'leave' | 'rest_day' | 'holiday' | 'time_off_summary'

export type GridClipboardPayload =
  | { kind: 'shift'; shift: Shift }
  | { kind: 'rest_day'; restDay: RestDay }

export interface ShiftGridEvent extends TimeGridEvent {
  type: 'shift'
  clipboardPayload?: GridClipboardPayload
  shift: Shift
  isBackup: boolean
  isDraft: boolean
  conflicts: string[]
}

export interface LeaveGridEvent extends TimeGridEvent {
  type: 'leave'
  clipboardPayload?: GridClipboardPayload | undefined
  leave: Leave
}

export interface RestDayGridEvent extends TimeGridEvent {
  type: 'rest_day'
  clipboardPayload?: GridClipboardPayload
  restDay: RestDay
  isDraft: boolean
}

export interface HolidayGridEvent extends TimeGridEvent {
  type: 'holiday'
  clipboardPayload?: GridClipboardPayload
  holiday: Holiday
}

export interface TimeOffSummaryGridEvent extends TimeGridEvent {
  type: 'time_off_summary'
  clipboardPayload?: GridClipboardPayload
  absencesCount: number
  publishedRestDaysCount: number
  loading: boolean
  onClick: () => void
}

interface TransformTimeOffSummaryToEventOptions {
  slotIndex: number
  entityId: string
  start: Date
  end: Date
  absencesCount: number
  publishedRestDaysCount: number
  loading: boolean
  onClick: () => void
}

export type ExtendedGridEventAppearance =
  | ShiftGridEvent
  | LeaveGridEvent
  | RestDayGridEvent
  | HolidayGridEvent
  | TimeOffSummaryGridEvent

const transformShiftToEvent = (
  shift: Shift,
  entityId: string,
  shiftsConflicts: ShiftsConflicts,
  view: View,
  canManage: boolean
): ShiftGridEvent => {
  const fragment = shift.fragment
  const localStart = fragment?.localStartAt
    ? stringToDate(fragment.localStartAt, 'backend_datetime_without_timezone')
    : shift.currentStartDate
  const localEnd = fragment?.localEndAt
    ? stringToDate(fragment.localEndAt, 'backend_datetime_without_timezone')
    : shift.currentEndDate

  const useSingleDayPlacement = !view.isDaily
  const dayStart = localStart.clone().startOf('day')
  const eventStart = useSingleDayPlacement ? dayStart : localStart
  const eventEnd = useSingleDayPlacement ? dayStart.clone().add(1, 'day') : localEnd

  return {
    id: `shift-${shift.id}`,
    entityId,
    start: eventStart.toDate(),
    end: eventEnd.toDate(),
    type: 'shift',
    selectable: !shift.isBackup && canManage,
    clipboardPayload: { kind: 'shift', shift },
    conflicts: shiftsConflicts.has(shift.id) ? [shift.id] : [],
    isBackup: shift.isBackup,
    isDraft: shift.isDraft,
    shift,
  }
}

const transformLeaveToEvent = (
  leave: Leave,
  entityId: string,
  canManageLeaves: boolean
): LeaveGridEvent => {
  const startDate = leave.getStartOn().clone().startOf('day')
  const finishDate = (leave._finishOn ?? leave.getStartOn()).clone().startOf('day')
  const endDate = finishDate.clone().add(1, 'day')

  return {
    id: `leave-${leave.id}`,
    entityId,
    start: startDate.toDate(),
    end: endDate.toDate(),
    type: 'leave',
    selectable: canManageLeaves,
    clipboardPayload: undefined,
    leave,
  }
}

const transformRestDayToEvent = (
  restDay: RestDay,
  entityId: string,
  canManage: boolean
): RestDayGridEvent => {
  // Normalize to a calendar day key first (employee-local day), then rebuild
  // as local grid date to avoid cross-timezone spill into adjacent columns.
  const dayKey = i18nDateToString(restDay.currentDate, 'backend_datetime_without_timezone')
  const startDate = stringToDate(dayKey, 'backend_datetime_without_timezone')
    .startOf('day')
    .toDate()
  const endDate = stringToDate(dayKey, 'backend_datetime_without_timezone').endOf('day').toDate()

  return {
    id: `rest-day-${restDay.id}`,
    entityId,
    start: startDate,
    end: endDate,
    type: 'rest_day',
    selectable: canManage,
    clipboardPayload: { kind: 'rest_day', restDay },
    isDraft: restDay.isDraft,
    restDay,
  }
}

const transformHolidayToEvent = (holiday: Holiday, entityId: string): HolidayGridEvent => {
  const startDate = holiday.date.toDate()
  const endDate = holiday.date.clone().add(24, 'hours').toDate()

  return {
    id: `holiday-${holiday.id}`,
    entityId,
    start: startDate,
    end: endDate,
    type: 'holiday',
    selectable: false,
    clipboardPayload: undefined,
    holiday,
  }
}

const transformTimeOffSummaryToEvent = ({
  slotIndex,
  entityId,
  start,
  end,
  absencesCount,
  publishedRestDaysCount,
  loading,
  onClick,
}: TransformTimeOffSummaryToEventOptions): TimeOffSummaryGridEvent => {
  return {
    id: `time-off-summary-${slotIndex}`,
    entityId,
    start,
    end,
    type: 'time_off_summary',
    selectable: false,
    clipboardPayload: undefined,
    absencesCount,
    publishedRestDaysCount,
    loading,
    onClick,
  }
}

export {
  transformShiftToEvent,
  transformLeaveToEvent,
  transformRestDayToEvent,
  transformHolidayToEvent,
  transformTimeOffSummaryToEvent,
}
